import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getUserData } from "../utils/auth-helpers";
import {
  getAccountIdFromPathname,
  isGlobalPath,
  replaceAccountInPathname,
} from "../utils/account-context";
import {
  applyAccountTheme,
  ACCOUNT_BRANDING_UPDATED_EVENT,
  normalizeAccountTheme,
  saveAccountTheme,
  savePersistedThemeMode,
} from "../utils/account-branding-theme";
import { getIsSuperAdminView } from "../utils/view-mode";
import useActiveAccount from "../hooks/useActiveAccount";
import useAccountBranding from "../hooks/useAccountBranding";
import useApplyThemeFromBranding from "../hooks/useApplyThemeFromBranding";
import { FetchTenantBootstrap } from "../services/auth";
import { buildPostSwitchAppOrigin } from "../utils/tenant-app-origin";

const AccountBrandingContext = createContext(null);
const ActiveAccountContext = createContext(null);

export const AccountBrandingProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [tenantBootstrap, setTenantBootstrap] = useState(null);
  const invalidAccountToastShownRef = useRef(false);
  const { activeAccountId, accountName, isValidAccount, fallbackAccountId } = useActiveAccount({ availableAccounts });
  const { branding, setBranding, refreshBranding, isLoading, error } = useAccountBranding(activeAccountId);

  useApplyThemeFromBranding({ accountId: activeAccountId, branding, selectedMode: branding?.mode });

  const switchAccount = useCallback(
    (nextAccountId, options = {}) => {
      if (!nextAccountId) {
        return null;
      }
      const replace = options.replace !== false;
      const preserveSearch = options.preserveSearch !== false;
      const search = preserveSearch ? location.search || "" : "";
      const nextPath = replaceAccountInPathname(nextAccountId, location.pathname);

      const hasExplicitHostSlug = Object.prototype.hasOwnProperty.call(options, "targetHostSlug");
      const slugFromList = availableAccounts.find((a) => String(a.id) === String(nextAccountId))?.host_slug;
      const targetSlug = hasExplicitHostSlug ? options.targetHostSlug : slugFromList;
      const normalizedTarget =
        typeof targetSlug === "string" && targetSlug.trim() ? targetSlug.trim().toLowerCase() : null;

      if (typeof window !== "undefined") {
        const nextOrigin = buildPostSwitchAppOrigin(window.location, normalizedTarget);
        if (nextOrigin) {
          window.location.replace(`${nextOrigin}${nextPath}${search}`);
          return nextPath;
        }
      }

      navigate(`${nextPath}${search}`, { replace });
      return nextPath;
    },
    [availableAccounts, location.pathname, location.search, navigate]
  );

  useEffect(() => {
    let mounted = true;
    const loadTenantBootstrap = async () => {
      const response = await FetchTenantBootstrap();
      if (!mounted || response?.error) {
        return;
      }
      const tenant = response?.tenant || null;
      setTenantBootstrap(tenant);
      if (typeof window !== "undefined") {
        if (tenant?.accountId) {
          localStorage.setItem("tv_tenant_account_id", String(tenant.accountId));
        } else {
          localStorage.removeItem("tv_tenant_account_id");
        }
      }
    };
    loadTenantBootstrap();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!tenantBootstrap?.accountId || tenantBootstrap?.allowAccountSwitch !== false) {
      return;
    }
    if (isGlobalPath(location.pathname)) {
      return;
    }
    const routeAccountId = getAccountIdFromPathname(location.pathname);
    const tenantAccountId = String(tenantBootstrap.accountId);
    if (routeAccountId && String(routeAccountId) !== tenantAccountId) {
      switchAccount(tenantAccountId, { replace: true });
    }
  }, [location.pathname, switchAccount, tenantBootstrap]);

  const userData = getUserData();
  const activeAccount = useMemo(
    () => ({
      id: activeAccountId,
      name: accountName || "",
      role: userData?.role || "",
      viewingAs: getIsSuperAdminView() ? "SuperAdmin" : "Account",
    }),
    [activeAccountId, accountName, userData?.role]
  );

  const shouldRedirectInvalidAccount = Boolean(
    fallbackAccountId && !isValidAccount && availableAccounts.length > 0
  );

  useEffect(() => {
    if (!shouldRedirectInvalidAccount) {
      return;
    }
    localStorage.setItem("accountId", String(fallbackAccountId));
    if (!invalidAccountToastShownRef.current) {
      invalidAccountToastShownRef.current = true;
      toast.info("Your previous account is no longer available. Switched to the first accessible account.");
    }
  }, [fallbackAccountId, shouldRedirectInvalidAccount]);

  const applyTheme = useCallback(
    (nextTheme, options = {}) => {
      const normalized = normalizeAccountTheme(typeof nextTheme === "function" ? nextTheme(branding) : nextTheme);
      setBranding(normalized);
      applyAccountTheme(normalized);

      const shouldPersistTheme = options.persistTheme !== false;
      if (shouldPersistTheme) {
        saveAccountTheme(activeAccountId, normalized);
      }

      const shouldPersistMode = options.persistMode !== false;
      if (activeAccountId && shouldPersistMode) {
        savePersistedThemeMode(activeAccountId, normalized.mode);
      }

      if (typeof window !== "undefined" && activeAccountId) {
        window.dispatchEvent(
          new CustomEvent(ACCOUNT_BRANDING_UPDATED_EVENT, {
            detail: { accountId: String(activeAccountId), theme: normalized },
          })
        );
      }

      return normalized;
    },
    [activeAccountId, branding, setBranding]
  );

  const setThemeMode = useCallback(
    (mode, options = {}) => {
      return applyTheme(
        (prev) => ({
          ...prev,
          mode,
          themeModeDefault: mode,
        }),
        options
      );
    },
    [applyTheme]
  );

  const activeAccountValue = useMemo(
    () => ({
      activeAccount,
      switchAccount,
      registerAvailableAccounts: setAvailableAccounts,
      tenantBootstrap,
      allowAccountSwitch: tenantBootstrap?.allowAccountSwitch !== false,
    }),
    [activeAccount, switchAccount, tenantBootstrap]
  );

  const brandingThemeValue = useMemo(
    () => ({
      branding,
      setBranding,
      refreshBranding,
      isBrandingLoading: isLoading,
      brandingError: error,
      applyTheme,
      setThemeMode,
    }),
    [branding, setBranding, refreshBranding, isLoading, error, applyTheme, setThemeMode]
  );

  const value = useMemo(
    () => ({
      ...activeAccountValue,
      ...brandingThemeValue,
    }),
    [activeAccountValue, brandingThemeValue]
  );

  if (shouldRedirectInvalidAccount) {
    const targetPath = replaceAccountInPathname(fallbackAccountId, location.pathname);
    return (
      <Navigate to={`${targetPath}${location.search || ""}`} replace />
    );
  }

  return (
    <ActiveAccountContext.Provider value={activeAccountValue}>
      <AccountBrandingContext.Provider value={value}>{children}</AccountBrandingContext.Provider>
    </ActiveAccountContext.Provider>
  );
};

AccountBrandingProvider.propTypes = {
  children: PropTypes.node,
};

export const useAccountBrandingContext = () => {
  const context = useContext(AccountBrandingContext);
  if (!context) {
    throw new Error("useAccountBrandingContext must be used within AccountBrandingProvider");
  }
  return context;
};

export const useActiveAccountId = () => {
  const activeContext = useContext(ActiveAccountContext);
  const brandingContext = useContext(AccountBrandingContext);

  if (activeContext?.activeAccount?.id) {
    return activeContext.activeAccount.id;
  }
  if (brandingContext?.activeAccount?.id) {
    return brandingContext.activeAccount.id;
  }
  if (typeof window !== "undefined") {
    return getAccountIdFromPathname(window.location.pathname);
  }
  return null;
};

export const useActiveAccountName = () => {
  const activeContext = useContext(ActiveAccountContext);
  const brandingContext = useContext(AccountBrandingContext);

  if (activeContext?.activeAccount?.name) {
    return activeContext.activeAccount.name;
  }
  if (brandingContext?.activeAccount?.name) {
    return brandingContext.activeAccount.name;
  }
  return "";
};

export default AccountBrandingContext;
